import { createContext, useContext, useEffect } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { Link as LinkDoRoteador, useInRouterContext, useLocation } from 'react-router-dom';
import { AuthCtx } from '../core/auth';
import { Icone, type ComponenteDeIcone } from './icones';
import { CampoDeData, eTipoDeData } from './seletores';
import { ThemeCtx } from './theme';
import type { Etiqueta as DescricaoDeEtiqueta, TomDeEtiqueta } from './etiquetas';

export {
  etiquetaDeTemperatura, etiquetaDeStatus, etiquetaDeOrigem, etiquetaDeReferrer, etiquetaDeCanal,
  etiquetaDeDispositivo, hostDeOrigem, TONS_DE_ETIQUETA,
} from './etiquetas';
export type { TomDeEtiqueta } from './etiquetas';

/* ---------------- Button ---------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** `graphite` é o `.btn--primario` do sistema: grafite, para onde o amarelo já está em uso no mesmo bloco. */
  /** `danger-ghost`: o remover discreto de cada linha de lista (F1, rodada 3). */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost' | 'graphite';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  /**
   * Só-ícone (componentes.html §04.1): quadrado no tamanho do alvo. Exige
   * `aria-label`, senão o botão é um quadrado mudo para quem lê por áudio.
   */
  icone?: boolean;
};

/**
 * Carregando (componentes.html §04.1): o rótulo some, o giro entra no lugar,
 * a largura é preservada e `aria-busy` diz o que está acontecendo. O texto
 * lido passa a ser "Carregando"; o rótulo fica transparente só para segurar
 * a largura.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  icone = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    `btn-${variant}`,
    size !== 'md' ? `btn-${size}` : '',
    icone ? 'btn--icone' : '',
    loading ? 'carregando' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading && <span className="sr">Carregando</span>}
      <span className="btn__rotulo" aria-hidden={loading || undefined}>{children}</span>
    </button>
  );
}

/* ---------------- Field ---------------- */

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  help?: string;
  error?: string;
  /** Confirmação do campo (endereço livre, domínio verificado). Some quando há erro. */
  sucesso?: string;
  /** Conferindo o valor no servidor: o giro entra no fim do campo e ele fica `aria-busy`. */
  carregando?: boolean;
  /** Prefixo fixo à esquerda do valor, como "R$" ou "https://" (componentes.html §04.2). */
  prefixo?: ReactNode;
  /** Sufixo fixo à direita, como ".myard.bio" ou "%". */
  sufixo?: ReactNode;
};

export function Field({ label, help, error, sucesso, carregando, id, prefixo, sufixo, ...rest }: FieldProps) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/\W+/g, '-')}`;
  const ok = !error && !carregando ? sucesso : undefined;
  const msg = error ?? ok ?? help;
  const temaDaPagina = useContext(ThemeCtx);
  const campo = temaDaPagina?.suiteClaro !== false && eTipoDeData(rest.type) ? <CampoDeData {...rest} type={rest.type} id={inputId} aria-invalid={error ? true : undefined} aria-describedby={msg ? `${inputId}-help` : undefined}/> : (
    <input
      id={inputId}
      className="input"
      aria-invalid={error ? true : undefined}
      aria-busy={carregando || undefined}
      aria-describedby={msg ? `${inputId}-help` : undefined}
      {...rest}
    />
  );
  /* O estado vive no fim do campo, onde o olho já está ao terminar de
     digitar: giro enquanto confere, visto quando deu certo. */
  const marcaDoFim = carregando
    ? <span className="fix campo__giro" aria-hidden="true" />
    : ok ? <span className="fix campo__ok" aria-hidden="true"><Icone.CheckCirculo tamanho={16} /></span> : null;
  const classe = ['field', error ? 'field-error' : '', ok ? 'field-success' : '', carregando ? 'field-carregando' : ''].filter(Boolean).join(' ');
  return (
    <div className={classe}>
      <label htmlFor={inputId}>{label}</label>
      {prefixo || sufixo || marcaDoFim ? (
        <div className="afixo">
          {prefixo && <span className="fix" aria-hidden="true">{prefixo}</span>}
          {campo}
          {sufixo && <span className="fix" aria-hidden="true">{sufixo}</span>}
          {marcaDoFim}
        </div>
      ) : (
        campo
      )}
      {msg && (
        <span className="help" id={`${inputId}-help`} role={error ? 'alert' : ok ? 'status' : undefined}>
          {msg}
        </span>
      )}
    </div>
  );
}

/* ---------------- Badge / Chip / Avatar ---------------- */

/**
 * Os tons vêm de `ui/etiquetas.ts`, que é onde a decisão de cor mora. `Badge`
 * só desenha: quem precisa saber "qual é a cor de `cold`" pergunta a
 * `etiquetaDeTemperatura` e passa o resultado para `<Etiqueta de={...} />`.
 */
type Tone = TomDeEtiqueta;

export function Badge({
  tone = 'neutral',
  dot,
  icone: IconeDaEtiqueta,
  aoRemover,
  nome,
  children,
}: {
  tone?: Tone;
  /**
   * O ícone do estado (fundamentos.html §01.3: "todo estado carrega ícone e
   * palavra"). Quando existe, ele ocupa o lugar do ponto: os dois juntos
   * seriam redundância da redundância. Na pílula neutra ele é o que
   * distingue "Rascunho" (lápis) de "Acesso direto" (globo) sem apelar à cor.
   */
  icone?: ComponenteDeIcone;
  /**
   * O ponto. Por padrão ele aparece em toda etiqueta COLORIDA e some na
   * neutra - passe `dot={false}` para calar, ou `dot` para forçar na neutra.
   *
   * POR QUE O PADRÃO MUDOU DE `false` PARA "colorida sim, neutra não"
   *
   *   O `componentes.html`, 04.8, explica o ponto assim: ele "existe para quem
   *   não distingue as cores: a forma e a palavra já bastam sem enxergar o
   *   tom". Ou seja, ele não é enfeite - é a redundância que faz "Pago" e
   *   "Recusado" continuarem distinguíveis para quem não separa verde de
   *   vermelho, que é perto de 8% dos homens.
   *
   *   Com o padrão em `false`, das 141 etiquetas do produto só 44 tinham
   *   ponto: as outras 97 estavam apoiadas só na cor. Cada uma delas seria
   *   uma decisão de acessibilidade tomada por esquecimento. Invertendo o
   *   padrão, o caminho fácil passa a ser o certo, e calar o ponto vira o que
   *   precisa ser escrito e justificado.
   *
   *   Na neutra ele não entra: cinza sobre cinza não distingue nada de nada, e
   *   um ponto que não carrega informação só rouba espaço do texto.
   */
  dot?: boolean;
  /**
   * Pílula removível (componentes.html §04.4): ganha um X que é botão de
   * verdade, com rótulo "Remover {texto}". Quem chama tira o item da lista.
   */
  aoRemover?: () => void;
  /** O nome do item, para o rótulo do X. Sem ele, o X diz só "Remover". */
  nome?: string;
  children: ReactNode;
}) {
  const mostrarPonto = !IconeDaEtiqueta && (dot ?? (tone !== 'neutral' && tone !== 'outline' && tone !== 'escuro'));
  return (
    <span className={`badge badge-${tone}`}>
      {IconeDaEtiqueta && <IconeDaEtiqueta tamanho={13} />}
      {mostrarPonto && <span className="dot" aria-hidden="true" />}
      {children}
      {aoRemover && (
        <button type="button" className="x" onClick={aoRemover} aria-label={nome ? `Remover ${nome}` : 'Remover'}>
          <Icone.Fechar tamanho={12} />
        </button>
      )}
    </span>
  );
}

/**
 * A pílula pronta a partir de uma etiqueta do mapa central (`ui/etiquetas.ts`):
 *
 *   <Etiqueta de={etiquetaDeTemperatura(contato.status)} />
 *   <Etiqueta de={etiquetaDeStatus(pagina.status)} />
 *   <Etiqueta de={etiquetaDeOrigem(contato.source)} />
 *
 * `sufixo` cola contagem ou detalhe ao rótulo ("Frio · até 29"), do jeito que
 * componentes.html §04.8 mostra "Morno · 61".
 */
export function Etiqueta({ de, sufixo, aoRemover }: { de: DescricaoDeEtiqueta; sufixo?: ReactNode; aoRemover?: () => void }) {
  return (
    <Badge tone={de.tom} icone={de.icone} nome={de.rotulo} aoRemover={aoRemover}>
      {de.rotulo}
      {sufixo != null && sufixo !== '' && <> · {sufixo}</>}
    </Badge>
  );
}

export function Chip({ children }: { children: ReactNode }) {
  return <span className="chip">{children}</span>;
}

/**
 * Avatar (componentes.html §04.9): iniciais sobre a família de cor do produto
 * em que a pessoa trabalha. 28, 40 e 56px. Com `foto`, a imagem recortada em
 * círculo; com `emLinha`, o ponto verde de 12px no canto.
 */
export function Avatar({
  name,
  small = false,
  tamanho,
  tom,
  foto,
  emLinha = false,
}: {
  name: string;
  /** Atalho antigo para `tamanho="sm"`. */
  small?: boolean;
  tamanho?: 'sm' | 'md' | 'lg';
  /** A família de cor. O padrão é o amarelo da Suite. */
  tom?: 'roxo' | 'ciano' | 'verde';
  foto?: string;
  emLinha?: boolean;
}) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0] ?? '')
    .join('')
    .toUpperCase();
  const t = tamanho ?? (small ? 'sm' : 'md');
  const cls = ['avatar', t === 'sm' ? 'avatar-sm' : '', t === 'lg' ? 'avatar-lg' : '', tom ? `avatar--${tom}` : '']
    .filter(Boolean)
    .join(' ');
  const desenho = (
    <div className={cls} title={name} aria-hidden={foto ? undefined : true}>
      {foto ? <img src={foto} alt={`Foto de ${name}`} /> : initials}
    </div>
  );
  if (!emLinha) return desenho;
  return (
    <span className="avatar-status" title={`${name}, em linha`}>
      {desenho}
    </span>
  );
}

/* ---------------- Banner ---------------- */

/** Um ícone por tom, no padrão `alerta--*` do design system (§04.19): o
 * triângulo de alerta serve tanto ao aviso (atenção) quanto ao erro - a cor
 * do tom já diferencia os dois; sucesso usa o círculo de confirmação. */
const ICONE_DO_TOM: Partial<Record<'info' | 'success' | 'warning' | 'danger', keyof typeof Icone>> = {
  info: 'InfoCirculo',
  success: 'CheckCirculo',
  warning: 'AlertaTriangulo',
  danger: 'AlertaTriangulo',
};

export function Banner({
  tone = 'info',
  title,
  icone,
  acoes,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  /** Passe `false` para calar o ícone padrão do tom. Cada tom tem o seu em
   * `ICONE_DO_TOM`, como os quatro alertas de componentes.html §04.19. */
  icone?: boolean;
  /**
   * O próximo passo em forma de botão (componentes.html §04.19): o alerta
   * fica onde o problema está e sempre carrega o que fazer a respeito.
   */
  acoes?: ReactNode;
  children: ReactNode;
}) {
  const nomeIcone = icone === false ? undefined : ICONE_DO_TOM[tone];
  const Ic = nomeIcone ? Icone[nomeIcone] : undefined;
  return (
    <div className={`banner banner-${tone}`} role={tone === 'danger' ? 'alert' : 'status'}>
      {Ic && (
        <span className="banner__icone" aria-hidden="true">
          <Ic tamanho={19} />
        </span>
      )}
      <span className="banner__corpo">
        {title && <span className="bt">{title}</span>}
        {children}
        {acoes && <span className="banner__acoes">{acoes}</span>}
      </span>
    </div>
  );
}

/* ---------------- Aviso flutuante (confirmação de salvar) ----------------
 *
 * Toda tela de configuração de Flow/Flow Pop-up salva campo a campo, no
 * `onBlur` - não existe um botão "Salvar" que reúna várias mudanças, porque
 * cada campo já vale sozinho assim que a pessoa sai dele. Sem um sinal
 * visível disso, a mudança parece não ter acontecido. Este é o sinal: um
 * aviso fixo no canto inferior direito (`.avisos`/`.aviso`, os mesmos do
 * resto do produto), que aparece a cada salvamento e some sozinho em 5s -
 * nunca empurra o layout da tela, ao contrário de um banner inline.
 */
export function AvisoFlutuante({
  mensagem,
  visivel,
  aoFechar,
}: {
  mensagem: string;
  visivel: boolean;
  /** Chamado 5s depois de aparecer - quem chama deve voltar `visivel` a false. */
  aoFechar?: () => void;
}) {
  useEffect(() => {
    if (!visivel) return;
    const t = setTimeout(() => aoFechar?.(), 5000);
    return () => clearTimeout(t);
  }, [visivel, aoFechar]);

  if (!visivel) return null;
  return (
    <div className="avisos">
      <div className="aviso aviso--ok on" role="status">
        <span className="aviso__texto">{mensagem}</span>
      </div>
    </div>
  );
}

/* ---------------- Indicador de salvo ----------------
 *
 * "Salvando..." / "Salvo" / "Não salvo" ao lado do título dos editores que
 * gravam sozinhos (landing, formulário, automação, editor do Bio). Nasceu no
 * MKT (F6) e subiu para o kit na F1, rodada 3. `role="status"` porque é
 * notícia, não interrupção; o "Não salvo" muda de cor E de texto.
 */
export function IndicadorDeSalvo({ salvando, erro, salvoEm }: {
  salvando: boolean;
  erro?: Error | null;
  /** Quando a última gravação terminou; `null` antes da primeira. */
  salvoEm: number | null;
}) {
  const estado = salvando ? 'salvando' : erro ? 'erro' : salvoEm ? 'salvo' : null;
  if (!estado) return null;
  return (
    <span className={`indicador-salvo indicador-salvo--${estado}`} role="status" aria-live="polite">
      <span className="indicador-salvo__ponto" aria-hidden="true" />
      {estado === 'salvando' ? 'Salvando...' : estado === 'erro' ? 'Não salvo' : 'Salvo'}
    </span>
  );
}

/* ---------------- Empty / Skeleton ---------------- */

export function EmptyState({
  title,
  description,
  action,
  icone,
  tipo = 'primeiro',
  as: Titulo = 'h4',
}: {
  title: string;
  description: string;
  action?: ReactNode;
  /** Icone do sistema. O padrao vem do `tipo`. */
  icone?: ReactNode;
  /**
   * Nivel do heading do titulo. O padrao é `h4`, correto quando a tela já
   * tem um `h1` em outro lugar. Uma tela cujo `EmptyState` É o corpo inteiro
   * (sem outro heading de primeiro nível) deve passar `as="h1"`.
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * As tres razoes de uma tela estar vazia, que pedem tres respostas
   * diferentes:
   *
   *   `primeiro`  não há dado ainda. A tela CONVIDA: é o único dos três em que
   *               cabe um botão de ação, porque há algo a criar.
   *   `busca`     há dado, o filtro é que não achou. A tela não convida a
   *               criar nada, ela sugere afrouxar a busca.
   *   `permissao` há dado e a pessoa não pode ver. Aqui não existe saída pela
   *               própria tela: a saída é pedir a alguém, e a tela diz a quem.
   *   `erro`      a lista não carregou. Não é vazio de verdade, e por isso
   *               não convida a criar: a ação é tentar de novo (F1, 26/10).
   *
   * Tratar os três com o mesmo desenho é o erro clássico: o botão "Criar
   * contato" aparecendo para quem só não tem permissão de ler contato.
   */
  tipo?: 'primeiro' | 'busca' | 'permissao' | 'erro';
}) {
  const PADRAO = {
    primeiro: <Icone.Mais />,
    busca: <Icone.Busca />,
    permissao: <Icone.Cadeado />,
    erro: <Icone.AlertaTriangulo />,
  } as const;

  return (
    <div className={`empty empty--${tipo}`} role={tipo === 'erro' ? 'alert' : undefined}>
      {/* Um "+" digitado não é o mesmo desenho que o "+" do sistema: pesa
          diferente do resto da tela porque vem da Poppins, e não da gramática
          de traço 1,75 dos 122 ícones. */}
      <div className="ei" aria-hidden="true">
        {icone ?? PADRAO[tipo]}
      </div>
      <Titulo>{title}</Titulo>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Skeleton({ width, height }: { width?: string; height?: string }) {
  return <div className="skeleton" style={{ width, height }} aria-hidden="true" />;
}

/**
 * Skeleton com a forma de uma tabela (doc 15.4: nunca spinner de página).
 *
 * Desenha uma `table.tbl` de verdade dentro do `.tbl-wrap`, com cabeçalho e
 * linhas nas mesmas medidas de célula da tabela carregada: quando os dados
 * chegam, a altura não muda e nada embaixo pula. Antes eram cinco barras
 * soltas, mais baixas que a tabela, e a página crescia de uma vez.
 */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  const colunas = Array.from({ length: Math.max(2, cols) }, (_, k) => k);
  return (
    <div className="tbl-wrap esq-tabela" role="status" aria-busy="true" aria-label="Carregando a lista">
      <table className="tbl" aria-hidden="true">
        <thead>
          <tr>
            {colunas.map((k) => (
              <th key={k}><Skeleton width={k === 0 ? '64px' : '48px'} height="10px" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, i) => (
            <tr key={i}>
              {colunas.map((k) => (
                <td key={k}>
                  {k === 0 ? (
                    <span className="esq-celula">
                      <Skeleton width="28px" height="28px" />
                      <span className="esq-bloco" style={{ flex: 1 }}>
                        <Skeleton width={`${78 - i * 6}%`} height="12px" />
                        <Skeleton width={`${50 - i * 4}%`} height="10px" />
                      </span>
                    </span>
                  ) : k === colunas.length - 1 ? (
                    <Skeleton width="72px" height="26px" />
                  ) : (
                    <Skeleton width={`${60 - ((i + k) % 3) * 12}%`} height="12px" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Skeleton com a forma de uma grade de cartões (doc 15.4: o esqueleto é fiel
 * ao layout final). `TableSkeleton` desenha linhas horizontais - errado para
 * uma tela cujo estado cheio é `.grid.grid-2` de `article.card`, como
 * Produtos, Coleções, Quizzes, Depoimentos e Imóveis: o esqueleto de tabela
 * dava um flash de forma errada antes do conteúdo real chegar.
 */
export function CardGridSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div className="grid grid-2" aria-hidden="true">
      {Array.from({ length: cards }, (_, i) => (
        <div className="card stack" key={i}>
          <Skeleton width="60%" height="17px" />
          <Skeleton width="35%" height="12px" />
          <Skeleton width="90%" height="13.5px" />
          <Skeleton width="70%" height="13.5px" />
        </div>
      ))}
    </div>
  );
}

/* ---------------- Stat ---------------- */

export function Stat({
  label, value, detail, icon,
}: { label: string; value: string; detail?: string; icon?: ReactNode }) {
  return (
    <div className="stat">
      {icon ? (
        <div className="stat-top">
          <span className="stat-ico" aria-hidden="true">{icon}</span>
          <div className="k">{label}</div>
        </div>
      ) : (
        <div className="k">{label}</div>
      )}
      <div className="v">{value}</div>
      {detail && <div className="d">{detail}</div>}
    </div>
  );
}

/* ---------------- PageHeader ---------------- */

/** Um número da faixa de contexto do topo. `tom` pinta só o `apoio`. */
export type NumeroDoTopo = {
  rotulo: string;
  valor: ReactNode;
  /** A linha de baixo: "de 25 no total", "caiu 12% em 30 dias". */
  apoio?: ReactNode;
  tom?: 'bom' | 'ruim';
};

/** Onde cada rota mora, para o primeiro degrau da trilha. A ordem importa:
 *  `/emails` é Marketing, mesmo fora de `/mkt`. */
const PRODUTO_DA_ROTA: { trecho: string; nome: string; icone: ComponenteDeIcone; raiz?: string }[] = [
  { trecho: 'home', nome: 'Início', icone: Icone.Painel },
  { trecho: 'crm', nome: 'CRM', icone: Icone.Negocio },
  { trecho: 'mkt', nome: 'Marketing', icone: Icone.Campanha },
  { trecho: 'emails', nome: 'Marketing', icone: Icone.Campanha, raiz: 'mkt' },
  { trecho: 'flw', nome: 'Flow', icone: Icone.ChatLateral },
  { trecho: 'pop', nome: 'Pop-up', icone: Icone.Balao },
  { trecho: 'bio', nome: 'Bio', icone: Icone.LinkBio },
  { trecho: 'mail', nome: 'Mail', icone: Icone.Enviados },
  { trecho: 'settings', nome: 'Configurações', icone: Icone.Engrenagem },
  { trecho: 'admin', nome: 'Console', icone: Icone.Relatorio },
];

/** Para quem desenha a tela fora da rota dela (a prévia da galeria `/_ds`):
 *  diz de que produto é, pelo mesmo trecho da rota (`crm`, `mkt`, `flw`...). */
export const ProdutoDoTopo = createContext<string | null>(null);

/** Ligado por quem já desenha a trilha acima da tela (a moldura de um site
 *  do Bio, "Bio › nome do site", com link): o PageHeader de dentro não
 *  repete a dele. */
export const TrilhaJaDesenhada = createContext(false);

/**
 * A trilha do topo, em pílula (a do YardHub: "← Identidade visual | Subponto
 * YARD DESIGN / YH-2026-530"): o botão grafite de voltar para a raiz do
 * produto, a pílula do produto na cor da família, o título da tela e, depois
 * de um fio, a organização e o degrau do meio em caixa alta miúda
 * ("CIMA EMPREENDIMENTOS / VENDAS").
 *
 * Só monta dentro de um roteador (a folha de telas e alguns testes desenham
 * o cabeçalho sem um). O link de voltar é calculado aqui mesmo, a partir da
 * rota, porque este arquivo não pode importar core/rotas: o slug do cliente
 * que vem antes do produto (`/cima/crm/...`) é mantido.
 */
function TrilhaDoTopo({ meio, titulo }: { meio?: string; titulo: string }) {
  const { pathname } = useLocation();
  const forcado = useContext(ProdutoDoTopo);
  const sessao = useContext(AuthCtx);
  // O slug do cliente vem antes (`/cima/crm/...`): procura o primeiro trecho
  // que é de um produto, em vez de olhar só o primeiro.
  const trechos = forcado ? [forcado] : pathname.split('/').filter(Boolean);
  const posicao = trechos.findIndex((s) => PRODUTO_DA_ROTA.some((p) => p.trecho === s));
  const produto = posicao >= 0 ? PRODUTO_DA_ROTA.find((p) => p.trecho === trechos[posicao]) : undefined;
  if (!produto) return null;
  const Desenho = produto.icone;
  const raiz = forcado ? null : `/${[...trechos.slice(0, posicao), produto.raiz ?? produto.trecho].join('/')}`;
  const naRaiz = raiz === pathname;
  const dados = [sessao?.org?.name, meio].filter(Boolean).join(' / ');
  return (
    <nav className="topo-pilula" aria-label="Você está em">
      {raiz && !naRaiz ? (
        <LinkDoRoteador to={raiz} className="topo-pilula__voltar" aria-label={`Voltar para ${produto.nome}`}>
          <Icone.SetaEsquerda tamanho={16} aria-hidden="true" />
        </LinkDoRoteador>
      ) : (
        <span className="topo-pilula__voltar topo-pilula__voltar--parado" aria-hidden="true">
          <Icone.SetaEsquerda tamanho={16} />
        </span>
      )}
      <span className="topo-pilula__produto">
        <i aria-hidden="true"><Desenho tamanho={12} /></i>
        {produto.nome}
      </span>
      {titulo !== produto.nome && <span className="topo-pilula__titulo" aria-current="page">{titulo}</span>}
      {dados && <span className="topo-pilula__dados">{dados}</span>}
    </nav>
  );
}

/**
 * O topo de toda tela do Suite (modelo A de docs/design/topo-das-secoes.html,
 * escolhido em 17/10/2026): a trilha de onde se está, o título com a frase e
 * a ação, e embaixo a FAIXA DE CONTEXTO - os três ou quatro números que
 * importam naquela tela e os filtros dela, num cartão só. Sem `numeros` nem
 * `filtros`, a faixa não aparece e o topo fica só com trilha e título.
 *
 * A trilha sai da rota (o produto) e do título; `trilha` acrescenta o degrau
 * do meio ("Vendas") e `trilha={false}` a esconde. Só o `h1` leva trilha e
 * faixa: um segundo `PageHeader` na mesma tela (`as="h2"`) é subtítulo.
 */
export function PageHeader({
  title,
  description,
  action,
  as: Titulo = 'h1',
  numeros,
  filtros,
  trilha,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  /**
   * Nível do heading do título. O padrão é `h1`. Uma tela que já usa um
   * `PageHeader` para o título principal e precisa de um segundo bloco
   * (ex.: uma segunda seção dentro da mesma página) deve passar `as="h2"`
   * nessa segunda ocorrência, para não duplicar `<h1>` na mesma página.
   */
  as?: 'h1' | 'h2' | 'h3';
  /** Os números da faixa de contexto. Três ou quatro; mais que isso vira painel. */
  numeros?: NumeroDoTopo[];
  /** Os filtros da tela, à direita na mesma faixa. */
  filtros?: ReactNode;
  /** O degrau do meio da trilha, ou `false` para não mostrar a trilha. */
  trilha?: string | false;
}) {
  const dentroDoRoteador = useInRouterContext();
  const trilhaAcima = useContext(TrilhaJaDesenhada);
  const principal = Titulo === 'h1';
  const temFaixa = principal && ((numeros?.length ?? 0) > 0 || !!filtros);
  // `.saud` + `.data` são o par de classes de tela-painel.html/tela-crm.html/
  // tela-flow.html para o topo de página - não o `<h1>` de 32px do sistema.
  // O heading semântico continua aqui (leitor de tela, estrutura do documento),
  // só troca de tamanho visual pela classe.
  return (
    <div className={`topo-secao${principal ? '' : ' topo-secao--sub'}${temFaixa ? ' topo-secao--faixa' : ''}`}>
      {principal && dentroDoRoteador && !trilhaAcima && trilha !== false && <TrilhaDoTopo meio={trilha || undefined} titulo={title} />}
      <div className="page-head">
        <div>
          <Titulo className="saud">{title}</Titulo>
          {description && <div className="data">{description}</div>}
        </div>
        {action && <div className="acoes">{action}</div>}
      </div>
      {temFaixa && (
        <div className="topo-faixa">
          {(numeros?.length ?? 0) > 0 && (
            <dl className="topo-faixa__numeros">
              {numeros!.map((n) => (
                <div key={n.rotulo} className="topo-faixa__numero">
                  <dt>{n.rotulo}</dt>
                  <dd className="topo-faixa__valor">{n.valor}</dd>
                  {n.apoio && <dd className={`topo-faixa__apoio${n.tom ? ` topo-faixa__apoio--${n.tom}` : ''}`}>{n.apoio}</dd>}
                </div>
              ))}
            </dl>
          )}
          {filtros && <div className="topo-faixa__filtros">{filtros}</div>}
        </div>
      )}
    </div>
  );
}

/* ---------------- Formatadores ---------------- */

export const brl = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(
    cents / 100,
  );

export const pct = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'percent', maximumFractionDigits: 1 }).format(v);

export const num = (v: number) => new Intl.NumberFormat('pt-BR').format(v);

export const dateTime = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));

/**
 * Data sem hora ('2026-10-12', como `deals.expected_at`, coluna `date`) é dia
 * do calendário, não instante. `new Date('2026-10-12')` lê como meia-noite
 * UTC, e no fuso de Brasília isso ainda é dia 11 às 21h - a ficha mostrava o
 * fechamento previsto um dia antes do gravado. Só a forma curta vira data
 * local; carimbo com hora continua passando por `new Date` como sempre.
 */
const SO_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;
export function lerDataOuInstante(iso: string): Date {
  const m = SO_DATA.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso);
}

export const date = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(lerDataOuInstante(iso));

export const time = (iso: string) =>
  new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short' }).format(new Date(iso));

/* ---------------- Gráfico ---------------- */

/**
 * A moldura acessível de todo gráfico (contrato, seção 8).
 *
 * Um gráfico feito de `<div>` com altura em porcentagem não é lido por ninguém:
 * o leitor de tela encontra caixas vazias. Aqui a caixa inteira vira uma imagem
 * com rótulo - `role="img"` mais `aria-label` - e a mesma informação sai também
 * como tabela, escondida da vista e presente para quem lê por áudio.
 *
 * A tabela não é enfeite de conformidade: é o único jeito de conferir um valor
 * exato num gráfico de barras, e serve a quem enxerga também, no `title` de
 * cada barra.
 */
export function Grafico({
  titulo,
  colunas,
  linhas,
  rotuloDireto = true,
  semTabela = false,
  children,
}: {
  /** O que o gráfico mostra, em uma frase. Vira o `aria-label`. */
  titulo: string;
  /** Cabeçalho da tabela alternativa. A primeira coluna é a categoria. */
  colunas: string[];
  /** Uma linha por categoria, na mesma ordem em que as barras aparecem. */
  linhas: (string | number)[][];
  /**
   * O rótulo direto: o valor escrito ao lado da série, sem obrigar a ler o
   * eixo. É o terceiro alívio que a paleta de dados exige de quem tem série
   * abaixo de 3:1 contra o fundo, junto do contorno da barra e desta tabela.
   */
  rotuloDireto?: boolean;
  /**
   * Sem o "Ver os dados em tabela". Só onde o mesmo número já está escrito
   * ao lado (a vitrine do site, um KPI com o gráfico de apoio); o `aria-label`
   * continua dizendo o que o gráfico mostra.
   */
  semTabela?: boolean;
  children: ReactNode;
}) {
  /* Um bloco só (F1, rodada 2): antes o gráfico, o rótulo direto e a tabela
     saíam como irmãos soltos, e quem punha o gráfico numa grade ganhava três
     células (achado da F2). */
  return (
    <div className="grafico-bloco">
      <div role="img" aria-label={titulo}>
        {children}
      </div>

      {/* Teto de quatro: acima disso o rótulo direto vira uma parede de números
          e atrapalha mais do que o eixo que ele substitui. */}
      {rotuloDireto && linhas.length > 0 && linhas.length <= 4 && (
        <ul className="rotulo-direto">
          {linhas.map((l) => (
            <li key={String(l[0])}>
              <span className="rotulo-direto__nome">{l[0]}</span>
              <span className="rotulo-direto__valor numeric">
                {l.slice(1).map((v, i) => (
                  <span key={i}>{typeof v === 'number' ? num(v) : v}</span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/*
        A tabela saiu de `.sr` e passou a viver num `<details>` fechado.

        Escondida, ela servia só a quem lê por áudio. Num `<details>` ela serve
        também a quem enxerga e precisa do valor exato - que é a pergunta que
        nenhum gráfico de barras responde - sem ocupar espaço de quem não
        precisa. O `<summary>` é focável e abre pelo teclado de graça.
      */}
      {!semTabela && <details className="tabela-do-grafico">
        <summary>Ver os dados em tabela</summary>
        <div className="tbl-wrap">
          <table className="tbl">
            <caption className="sr">{titulo}</caption>
            <thead>
              <tr>
                {colunas.map((c, i) => (
                  <th key={c} scope="col" className={i === 0 ? undefined : 'right'}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => (
                <tr key={i}>
                  {l.map((v, j) =>
                    j === 0 ? (
                      <th key={j} scope="row">
                        {v}
                      </th>
                    ) : (
                      <td key={j} className="right">
                        {typeof v === 'number' ? num(v) : v}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>}
    </div>
  );
}
